// appEvents.js — Lightweight cross-component and cross-tab event dispatcher for real-time UI sync

export const APP_MUTATION_EVENT = "reva_data_mutated";
const CHANNEL_NAME = "reva_sync_channel";

// Initialize BroadcastChannel if available in the browser environment
let broadcastChannel = null;
if (typeof window !== "undefined" && typeof BroadcastChannel !== "undefined") {
  try {
    broadcastChannel = new BroadcastChannel(CHANNEL_NAME);
  } catch (err) {
    console.warn("BroadcastChannel initialization warning:", err);
  }
}

/**
 * Notify the application (across all open tabs, windows, and components) that data was created, updated, or deleted.
 * @param {string} entity - e.g. "invoice", "product", "stock", "stock_receipt", "price", "all"
 */
export function emitDataMutation(entity = "all") {
  if (typeof window !== "undefined") {
    const payload = { entity, timestamp: Date.now() };

    // 1. Same-window / same-tab dispatch
    try {
      window.dispatchEvent(
        new CustomEvent(APP_MUTATION_EVENT, {
          detail: payload,
        })
      );
    } catch (_) {}

    // 2. Cross-window / cross-tab dispatch via BroadcastChannel
    if (broadcastChannel) {
      try {
        broadcastChannel.postMessage({ type: APP_MUTATION_EVENT, ...payload });
      } catch (err) {
        console.warn("Broadcast error:", err);
      }
    }

    // 3. Storage event fallback for older/restricted browser contexts
    try {
      localStorage.setItem("reva_last_mutation", JSON.stringify(payload));
    } catch (_) {}
  }
}

/**
 * Subscribe to mutations across components and across split browser tabs/windows.
 * @param {Function} callback - function({ entity, timestamp })
 * @returns {Function} cleanup / unsubscribe function
 */
export function subscribeDataMutation(callback) {
  if (typeof window === "undefined") return () => {};

  // 1. Same-window event listener
  const handleLocal = (e) => {
    callback(e.detail || { entity: "all", timestamp: Date.now() });
  };
  window.addEventListener(APP_MUTATION_EVENT, handleLocal);

  // 2. Cross-window BroadcastChannel listener
  const handleBroadcast = (event) => {
    if (event?.data?.type === APP_MUTATION_EVENT) {
      callback({
        entity: event.data.entity || "all",
        timestamp: event.data.timestamp || Date.now(),
      });
    }
  };
  if (broadcastChannel) {
    broadcastChannel.addEventListener("message", handleBroadcast);
  }

  // 3. Storage event listener (fires when localStorage is updated in another tab)
  const handleStorage = (e) => {
    if (e.key === "reva_last_mutation" && e.newValue) {
      try {
        const parsed = JSON.parse(e.newValue);
        callback(parsed);
      } catch (_) {}
    }
  };
  window.addEventListener("storage", handleStorage);

  // Cleanup on unmount
  return () => {
    window.removeEventListener(APP_MUTATION_EVENT, handleLocal);
    if (broadcastChannel) {
      broadcastChannel.removeEventListener("message", handleBroadcast);
    }
    window.removeEventListener("storage", handleStorage);
  };
}

